/* SIZESOFTX
=========================================================================================
* Descripción: Context para el módulo de IA de entrenamiento de artículos
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 25/04/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Normalización de imagen de artículo para PC y móvil
*      3        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Compatibilidad con respuesta de guardado de imagen
*      4        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Consumo de ficha e imagen desde backend IA
*      5        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Normalización robusta de clave e imagen IA
*      6        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Conservación de descripción de artículo en selección IA
*      7        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Imagen principal de artículo desde backend IA
*      8        | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Carga explícita de aprendizaje por clave de artículo
*      9        | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Predicción IA silenciosa para monitoreo visual
*      10       | 02/06/2026 | Lic. José Luis Aragón Cervantes  | Filtro de confianza mínima para predicción IA
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRequest } from "../System/RequestContext";
import { useLayout } from "../System/LayoutContext";

import { 
    apiIAInfoAprendizaje, 
    apiIAAgragarVector, 
    apiIAPredecir,
    apiIAExportarModelo,
    apiIAEliminarEntrenamiento,
    apiIAArticuloPorClave,
    apiIAGuardarImagen,
    apiIAEntrenarModelo,
    apiIAEntrenarEstado,
    apiIAMetricas,
    apiCatalogosGuardarImagen 
} from "../../settings/ApiConfig";

export const IAEntrenamientoContext = createContext();
export const IAEntrenamientoProvider = (props) => {
    const { GetRequest, PostRequest, DeleteRequest } = useRequest();
    const { handleOpenAlert } = useLayout();
    const navigate = useNavigate();

    const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
    const [listaArticulos, setListaArticulos] = useState([]);
    const [infoAprendizaje, setInfoAprendizaje] = useState([]);
    const [imagenCapturada, setImagenCapturada] = useState(null);
    const [capturando, setCapturando] = useState(false);
    const [tipoDispositivo, setTipoDispositivo] = useState("PC");
    const [statusBuscarArticuloSimple, setStatusBuscarArticuloSimple] = useState(false);
    const [imagenArticulo, setImagenArticulo] = useState(null);
    const [medidaSeleccionada, setMedidaSeleccionada] = useState("");
    const [metricasIA, setMetricasIA] = useState(null);
    const [entrenamientoRed, setEntrenamientoRed] = useState({
        activo: false,
        jobId: "",
        dominio: "articulos",
        procesadas: 0,
        totales: 0,
        loss: 0,
        mensaje: "",
    });

    // Medidas del artículo (ficha IA listamedidas/lMedidas o catálogo medidas):
    // el flujo de aprendizaje entrena hasta 3 medidas base por artículo.
    const getMedidasArticulo = (articulo) => {
        const raw = articulo?.listamedidas
            || articulo?.lMedidas
            || articulo?.medidas
            || articulo?.Medidas
            || [];
        const arr = Array.isArray(raw) ? raw : [];
        const out = [];
        for (const m of arr) {
            const v = typeof m === "string" || typeof m === "number"
                ? m
                : (m?.medida ?? m?.dMedida ?? m?.sMedida ?? m?.Medida ?? m?.smedida ?? "");
            const s = String(v ?? "").trim();
            if (s && !out.includes(s)) out.push(s);
        }
        return out;
    };

    const bytesToBase64 = (bytes) => {
        if (!Array.isArray(bytes)) return "";
        let binary = "";
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    const normalizeImageDataUrl = (imageValue) => {
        if (!imageValue) return null;

        if (Array.isArray(imageValue)) {
            const base64 = bytesToBase64(imageValue);
            return base64 ? `data:image/png;base64,${base64}` : null;
        }

        if (typeof imageValue === "string") {
            const value = imageValue.trim();
            if (!value) return null;
            if (value.startsWith("data:image/")) return value;
            return `data:image/png;base64,${value}`;
        }

        return null;
    };

    const getArticuloImageDataUrl = (articulo) => {
        const imageValue = articulo?.imagen
            || articulo?.imagenbase64
            || articulo?.bImagen
            || articulo?.byImagen
            || articulo?.imagenBase64
            || articulo?.ImagenBase64
            || articulo?.sImagenBase64
            || articulo?.SImagenBase64;

        return normalizeImageDataUrl(imageValue);
    };

    const getArticuloClave = (articulo) => {
        return articulo?.clavearticulo
            || articulo?.claveArticulo
            || articulo?.ClaveArticulo
            || articulo?.sClaveArticulo
            || articulo?.SClaveArticulo
            || "";
    };

    const getArticuloDescripcion = (articulo) => {
        return articulo?.descripcion
            || articulo?.descripcionarticulo
            || articulo?.descripcionArticulo
            || articulo?.DescripcionArticulo
            || articulo?.sDescripcion
            || articulo?.SDescripcion
            || "";
    };

    const responseHasError = (response) => {
        return Boolean(response?.hasError || response?.haserror);
    };

    const getResponseErrorCode = (response) => {
        return response?.errorCode ?? response?.errorcode ?? response?.httpCode ?? response?.httpcode ?? 0;
    };

    const handleAbrirBuscarArticulo = () => {
        setStatusBuscarArticuloSimple(true);
    };

    const handleCerrarBuscarArticulo = () => {
        setStatusBuscarArticuloSimple(false);
    };

    const handleObtenerArticuloByClave = async (clave) => {
        if (!clave) return null;
        
        const claveLimpia = clave.trim().toUpperCase();
        let url = apiIAArticuloPorClave.replace("{clavearticulo}", encodeURIComponent(claveLimpia));
        const response = await GetRequest({
            url,
            loader: "Obteniendo datos de artículo IA",
        });

        const result = response?.result?.articulo || response?.result;

        if (responseHasError(response) || !result || getResponseErrorCode(response) === 404) {
            console.warn("[IA][ArticuloPorClave] Artículo no encontrado en IA", {
                clave: claveLimpia,
                response,
            });
            return null;
        }

        const articulo = {
            ...result,
            clavearticulo: getArticuloClave(result) || claveLimpia,
            descripcion: getArticuloDescripcion(result),
        };

        const imagenDataUrl = getArticuloImageDataUrl(articulo);
        setImagenArticulo(imagenDataUrl);

        setArticuloSeleccionado(articulo);
        return articulo;
    };

    const handleArticuloSelect = async (data) => {
        const claveArticulo = getArticuloClave(data);
        const claveLimpia = claveArticulo?.trim().toUpperCase() || "";
        const imagenCatalogo = getArticuloImageDataUrl(data);
        const articuloIA = await handleObtenerArticuloByClave(claveLimpia);
        const imagenIA = getArticuloImageDataUrl(articuloIA);
        const descripcionArticulo = getArticuloDescripcion(data);

        if (articuloIA) {
            setArticuloSeleccionado((prev) => ({
                ...prev,
                ...articuloIA,
                descripcion: getArticuloDescripcion(articuloIA) || descripcionArticulo,
            }));
        }

        if (!articuloIA && claveLimpia) {
            setArticuloSeleccionado({
                clavearticulo: claveLimpia,
                descripcion: descripcionArticulo,
                marca: data.marca || ""
            });
        }

        setImagenArticulo(imagenIA || imagenCatalogo || null);

        if (claveLimpia) {
            await handleObtenerInfoAprendizaje(claveLimpia);
        }

        handleCerrarBuscarArticulo();

        // Medida por defecto: primera de la ficha (flujo de 3 medidas base).
        const medidas = getMedidasArticulo(articuloIA || data);
        setMedidaSeleccionada(medidas[0] || "");
    };

    const handleUploadImage = (event) => {
        const files = event?.target?.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        const reader = new FileReader();

        reader.onloadend = () => {
            const result = reader.result;
            if (!result) return;

            setImagenCapturada(result);
            setImagenArticulo(result);
        };

        reader.readAsDataURL(file);
    };

    const handleCambiarTipoDispositivo = (tipo) => {
        setTipoDispositivo(tipo);
    };

    const handleSeleccionarArticulo = (articulo) => {
        setArticuloSeleccionado(articulo);
    };

    const handleCapturarImagen = (imagenBase64) => {
        setImagenCapturada(imagenBase64);
    };

    const handleGuardarImagenArticulo = async (imagenBase64, articuloOverride = null) => {
        const articuloParaGuardar = articuloOverride || articuloSeleccionado;
        if (!articuloParaGuardar) {
            handleOpenAlert("Seleccione un artículo primero");
            return false;
        }

        if (!imagenBase64) {
            handleOpenAlert("Capture una imagen primero");
            return false;
        }

        try {
            const base64 = imagenBase64.split(',')[1] || imagenBase64;
            const claveArticulo = articuloParaGuardar.clavearticulo?.trim().toUpperCase() || '';
            const descripcion = getArticuloDescripcion(articuloParaGuardar);

            const result = await PostRequest({
                url: apiIAGuardarImagen,
                body: {
                    clavearticulo: claveArticulo,
                    descripcion,
                    imagenbase64: base64
                },
                loader: "Guardando imagen en IA..."
            });

            if (responseHasError(result)) {
                handleOpenAlert("Error al guardar imagen: " + result.message);
                return false;
            }

            const imagenGuardada = result?.result?.imagenbase64
                || result?.result?.imagenBase64
                || result?.result?.ImagenBase64;

            const imagenActualizada = normalizeImageDataUrl(imagenGuardada) || imagenBase64;
            setImagenCapturada(imagenActualizada);
            setImagenArticulo(imagenActualizada);

            try {
                const catalogosResult = await PostRequest({
                    url: apiCatalogosGuardarImagen,
                    body: {
                        clavearticulo: claveArticulo,
                        imagenbase64: base64
                    },
                    loader: "Actualizando imagen en catálogo de artículos..."
                });
                if (responseHasError(catalogosResult)) {
                    handleOpenAlert("La imagen se guardó en IA pero no se pudo actualizar en el catálogo de artículos");
                }
            } catch (err) {
                handleOpenAlert("La imagen se guardó en IA pero no se pudo actualizar en el catálogo de artículos");
            }

            return true;
        } catch (err) {
            handleOpenAlert("Error al guardar imagen");
            return false;
        }
    };

    const handleEnviarVectorEntrenamiento = async (imagenBase64, articuloOverride = null, options = {}) => {
        const articuloParaEntrenar = articuloOverride || articuloSeleccionado;
        if (!articuloParaEntrenar) {
            handleOpenAlert("Seleccione un artículo primero");
            return;
        }

        if (!imagenBase64) {
            handleOpenAlert("Capture una imagen primero");
            return;
        }

        const claveArticulo = articuloParaEntrenar.clavearticulo?.trim().toUpperCase() || '';
        const medida = (options.medida ?? medidaSeleccionada ?? "").toString().trim().toUpperCase();

        const result = await PostRequest({
            url: apiIAAgragarVector,
            body: {
                claveArticulo: claveArticulo,
                descripcion: getArticuloDescripcion(articuloParaEntrenar),
                imagen: imagenBase64,
                medida: medida
            },
            loader: options.silent ? "" : "Enviando vector de entrenamiento..."
        });

        if (responseHasError(result)) {
            if (!options.silent) handleOpenAlert("Error al enviar entrenamiento: " + result.message);
            return null;
        }

        if (!options.silent) handleOpenAlert(`Muestra guardada${medida ? ` (medida ${medida})` : ""}`, "ok");
        setImagenCapturada(null);
        await handleObtenerInfoAprendizaje(claveArticulo);
        return result?.result ?? null;
    };

    const handleObtenerInfoAprendizaje = async (claveArticulo = "") => {
        const claveLimpia = claveArticulo?.trim().toUpperCase() || '';
        const url = claveLimpia 
            ? apiIAInfoAprendizaje.replace("{claveArticulo?}", claveLimpia)
            : apiIAInfoAprendizaje.replace("/{claveArticulo?}", "");

        const result = await GetRequest({
            url: url,
            loader: "Cargando información de aprendizaje..."
        });

        if (responseHasError(result)) {
            handleOpenAlert("Error al obtener info de aprendizaje: " + result.message);
            return;
        }

        const aprendizaje = Array.isArray(result.result)
            ? result.result.map((item) => ({
                ...item,
                clavearticulo: getArticuloClave(item)?.trim().toUpperCase() || "",
                descripcion: getArticuloDescripcion(item),
                totalvectores: item?.totalvectores ?? item?.totalVectores ?? item?.iTotalVectores ?? 0,
                vectoresv2: item?.vectoresv2 ?? item?.vectoresV2 ?? item?.iVectoresV2 ?? 0,
                porcentaje: item?.porcentaje ?? item?.dPorcentaje ?? 0,
                medidas: Array.isArray(item?.medidas ?? item?.lMedidas)
                    ? (item.medidas ?? item.lMedidas).map((m) => ({
                        medida: String(m?.medida ?? m?.sMedida ?? "").trim(),
                        vectores: Number(m?.vectores ?? m?.iVectores ?? 0),
                    })).filter((m) => m.medida)
                    : [],
            }))
            : [];

        setInfoAprendizaje(aprendizaje);
    };

    const handleExportarModelo = async () => {
        const result = await GetRequest({
            url: apiIAExportarModelo,
            loader: "Exportando modelo..."
        });

        if (responseHasError(result)) {
            handleOpenAlert("Error al exportar modelo: " + result.message);
            return;
        }

        handleOpenAlert("Modelo exportado correctamente", "ok");
        return result.result;
    };

    // Entrena la red neuronal en el servidor (job async con progreso).
    const handleEntrenarModelo = async (dominio = "articulos", epocas = 8) => {
        if (entrenamientoRed.activo) {
            handleOpenAlert("Ya hay un entrenamiento en curso");
            return null;
        }
        const inicio = await PostRequest({
            url: apiIAEntrenarModelo,
            body: { dominio, epocas },
            loader: "Iniciando entrenamiento de la red..."
        });
        if (responseHasError(inicio)) {
            handleOpenAlert("Error al iniciar entrenamiento: " + inicio.message);
            return null;
        }
        const jobId = inicio?.result?.jobId || inicio?.result?.jobid || "";
        if (!jobId) {
            handleOpenAlert("El servidor no devolvió folio de entrenamiento");
            return null;
        }
        setEntrenamientoRed({
            activo: true, jobId, dominio,
            procesadas: 0, totales: 0, loss: 0,
            mensaje: "Entrenando red neuronal...",
        });

        for (let i = 0; i < 200; i++) {
            await new Promise((r) => setTimeout(r, 3000));
            const estado = await GetRequest({
                url: apiIAEntrenarEstado.replace("{jobId}", encodeURIComponent(jobId)),
                loader: ""
            });
            if (responseHasError(estado)) continue;
            const job = estado?.result || {};
            const fin = job?.estado === "completado" || job?.estado === "error";
            setEntrenamientoRed({
                activo: !fin,
                jobId,
                dominio,
                procesadas: Number(job?.ramasProcesadas ?? job?.ramasprocesadas ?? 0),
                totales: Number(job?.ramasTotales ?? job?.ramastotales ?? 0),
                loss: Number(job?.loss ?? 0),
                mensaje: job?.mensaje || job?.estado || "",
            });
            if (fin) {
                if (job?.estado === "completado") {
                    handleOpenAlert(job?.mensaje || "Red entrenada correctamente", "ok");
                    await handleObtenerMetricas(dominio);
                } else {
                    handleOpenAlert("Entrenamiento falló: " + (job?.mensaje || "error"));
                }
                return job;
            }
        }
        setEntrenamientoRed((prev) => ({ ...prev, activo: false, mensaje: "Tiempo de espera agotado; consulte métricas" }));
        return null;
    };

    const handleObtenerMetricas = async (dominio = "articulos") => {
        const url = apiIAMetricas.replace("{dominio?}", encodeURIComponent(dominio));
        const result = await GetRequest({ url, loader: "" });
        if (responseHasError(result)) return null;
        setMetricasIA(result?.result || null);
        return result?.result || null;
    };

    const handlePredecir = async (imagenBase64, options = {}) => {
        const medida = (options.medida ?? "").toString().trim();
        const url = medida
            ? `${apiIAPredecir}?medida=${encodeURIComponent(medida.toUpperCase())}`
            : apiIAPredecir;
        const result = await PostRequest({
            url,
            body: imagenBase64.split(',')[1],
            loader: options.silent ? "" : "Analizando imagen..."
        });

        if (responseHasError(result)) {
            if (!options.silent) {
                handleOpenAlert("Error al predecir: " + result.message);
            }
            return null;
        }

        const confidence = Number(result.result?.confianza ?? 0);
        if (options.minConfidence !== undefined && (!result.result || confidence < Number(options.minConfidence))) {
            return null;
        }

        return result.result;
    };

    const handleLimpiarCaptura = () => {
        setImagenCapturada(null);
    };

    const handleEliminarEntrenamiento = async () => {
        if (!articuloSeleccionado) {
            handleOpenAlert("Seleccione un artículo primero");
            return false;
        }

        const claveArticulo = articuloSeleccionado.clavearticulo?.trim().toUpperCase() || '';

        const result = await DeleteRequest({
            url: apiIAEliminarEntrenamiento.replace("{claveArticulo}", claveArticulo),
            loader: "Eliminando entrenamiento..."
        });

        if (responseHasError(result)) {
            handleOpenAlert("Error al eliminar entrenamiento: " + result.message);
            return false;
        }

        handleOpenAlert("Entrenamiento eliminado correctamente", "ok");
        await handleObtenerInfoAprendizaje(claveArticulo);
        return true;
    };

    return (
        <IAEntrenamientoContext.Provider
            value={{
                articuloSeleccionado,
                setArticuloSeleccionado,
                listaArticulos,
                setListaArticulos,
                infoAprendizaje,
                setInfoAprendizaje,
                imagenCapturada,
                setImagenCapturada,
                capturando,
                setCapturando,
                tipoDispositivo,
                handleCambiarTipoDispositivo,
                handleSeleccionarArticulo,
                getArticuloImageDataUrl,
                handleCapturarImagen,
                handleEnviarVectorEntrenamiento,
                handleObtenerInfoAprendizaje,
                handleExportarModelo,
                handlePredecir,
                handleLimpiarCaptura,
                statusBuscarArticuloSimple,
                handleAbrirBuscarArticulo,
                handleCerrarBuscarArticulo,
                handleObtenerArticuloByClave,
                handleArticuloSelect,
                handleUploadImage,
                imagenArticulo,
                setImagenArticulo,
                handleGuardarImagenArticulo,
                handleEliminarEntrenamiento,
                medidaSeleccionada,
                setMedidaSeleccionada,
                getMedidasArticulo,
                metricasIA,
                handleObtenerMetricas,
                entrenamientoRed,
                handleEntrenarModelo,
            }}
        >
            {props.children}
        </IAEntrenamientoContext.Provider>
    );
};

export const useIAEntrenamiento = () => useContext(IAEntrenamientoContext);
